import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';
import backendUrl from '../BackendUrl';

// Create the UserContext
export const UserContext = createContext();

// Create the UserProvider
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(null);
  const [adminToken, setAdminToken] = useState(null);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');

  // ========================
  // SIGN OUT (GLOBAL)
  // ========================
  const signOut = () => {
    setUser(null);
    setAuthToken(null);
    setAdminToken(null);

    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('adminToken');

    window.dispatchEvent(new Event('authchange'));
    window.dispatchEvent(new Event('adminchange'));
  };

  // ========================
  // SIGN IN
  // ========================
  const signIn = async (email, password) => {

    // 👉 FIRST: if user already has refresh token, try to restore session
    const existingRefresh = localStorage.getItem('refreshToken');

    if (existingRefresh) {
      try {
        const refreshRes = await axios.post(
          `${backendUrl}/api/accounts/refresh`,
          { refreshToken: existingRefresh }
        );

        const newToken = refreshRes.data.token;
        localStorage.setItem('authToken', newToken);

        const storedUser = JSON.parse(localStorage.getItem('user'));

        setUser(storedUser);
        setAuthToken(newToken);

        return { success: true, user: storedUser };
      } catch {
        // Refresh invalid → continue to real login below
      }
    }

    // ===== NORMAL LOGIN =====
    try {
      const response = await axios.post(`${backendUrl}/api/accounts/login`, {
        email,
        password,
      });

      const { user, token, refreshToken, adminToken } = response.data;

      setUser(user);
      setAuthToken(token);

      if (adminToken) {
        setAdminToken(adminToken);
        localStorage.setItem('adminToken', adminToken);
      }

      localStorage.setItem('authToken', token);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(user));

      return { success: true, user };

    } catch (error) {
      console.error('Sign-in error:', error.response?.data?.message || error.message);

      if (
        error.response?.status === 403 &&
        error.response?.data?.message === 'Account not verified'
      ) {
        return { success: false, verified: false, email };
      }

      throw new Error(error.response?.data?.message || 'Failed to sign in.');
    }
  };

  // ========================
  // VALIDATE TOKEN ON APP LOAD
  // ========================
  useEffect(() => {
    const validateSession = async () => {
      const storedToken = localStorage.getItem('authToken');
      const storedUser = localStorage.getItem('user');
      const storedAdminToken = localStorage.getItem('adminToken');

      if (!storedToken || !storedUser) return;

      try {
        // 🔐 Validate token with backend
        const tokenToValidate = storedAdminToken || storedToken;
        await axios.get(`${backendUrl}/api/accounts/admin`, {
          headers: { Authorization: `Bearer ${tokenToValidate}` },
        });

        // Token is valid → restore session
        setAuthToken(storedToken);
        setUser(JSON.parse(storedUser));

        if (storedAdminToken) {
          setAdminToken(storedAdminToken);
        }

      } catch (err) {
        // ❌ Token expired or invalid
        console.warn('Stored session invalid – logging out');
        signOut();
      }
    };

    validateSession();
  }, []);

  // ========================
  // THEME PERSIST
  // ========================
  useEffect(() => {
    localStorage.setItem('theme', theme);
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  // ========================
  // AUTH FETCH (NO SILENT REFRESH)
  // ========================
  const authFetch = async ({ url, method = 'get', data = null }) => {
    const token = localStorage.getItem('authToken');

    try {
      return await axios({
        url: backendUrl + url,
        method,
        data,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });

    } catch (error) {
      if (error.response?.status === 401) {
        // 🚨 Any expired token during use = REAL logout
        signOut();
      }

      throw error;
    }
  };

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        authToken,
        adminToken,
        signIn,
        signOut,
        theme,
        setTheme,
        authFetch,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};